// @ts-nocheck

import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { FileUpload } from '@/components/ui/file-upload';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { addKnowledgeBaseDocument } from '@/service/knowledgeBaseApi';
import { KNOWLEDGE_BASE_MAX_FILE_SIZE_BYTES, KNOWLEDGE_BASE_ACCEPT } from '@/utilities/constant';
import { validateKnowledgeBaseFile } from '@/utilities/knowledgeBaseFile';

const SOURCE_TYPES = [
  { value: 'file', label: 'Upload file' },
  { value: 'url', label: 'Website / URL' },
];

const MAX_FILE_LABEL = `${(KNOWLEDGE_BASE_MAX_FILE_SIZE_BYTES / 1024 / 1024).toFixed(0)} MB`;

export default function AddDocumentDialog({ open, onOpenChange, kbId, kbName, onAdded }: any) {
  const [sourceType, setSourceType] = useState('file');
  const [url, setUrl] = useState('');
  const [file, setFile] = useState<File[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const reset = () => {
    setSourceType('file');
    setUrl('');
    setFile([]);
    setError('');
  };

  const close = () => {
    reset();
    onOpenChange(false);
  };

  const handleFileChange = async (files: File[]) => {
    if (!files || files.length === 0) {
      setFile([]);
      return;
    }
    const fileError = await validateKnowledgeBaseFile(files[0]);
    if (fileError) {
      setFile([]);
      setError(fileError);
      toast.error(fileError);
      return;
    }
    setFile(files);
    setError('');
  };

  const handleSubmit = async () => {
    if (sourceType === 'url' && !url.trim()) {
      setError('Enter a URL to ingest');
      return;
    }
    if (sourceType === 'file' && file.length === 0) {
      setError('Choose a file to upload');
      return;
    }
    if (sourceType === 'file') {
      const fileError = await validateKnowledgeBaseFile(file[0]);
      if (fileError) {
        setError(fileError);
        toast.error(fileError);
        return;
      }
    }
    setError('');
    setSaving(true);
    try {
      await addKnowledgeBaseDocument(kbId, {
        url: sourceType === 'url' ? url.trim() : undefined,
        file: sourceType === 'file' ? file[0] : undefined,
      });
      toast.success('Document queued for ingestion');
      close();
      onAdded?.();
    } catch (err: any) {
      toast.error(typeof err === 'string' ? err : 'Failed to add document');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => (!o ? close() : onOpenChange(o))}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Add Document</DialogTitle>
          <DialogDescription>
            Add another source to{' '}
            <span className="font-medium text-foreground">{kbName || 'this knowledge base'}</span>.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <RadioGroup
            value={sourceType}
            onValueChange={(v) => {
              setSourceType(v);
              setError('');
            }}
            className="grid grid-cols-1 gap-2 sm:grid-cols-2"
          >
            {SOURCE_TYPES.map((opt) => {
              const selected = sourceType === opt.value;
              return (
                <label
                  key={opt.value}
                  htmlFor={`add-doc-source-${opt.value}`}
                  className={`flex cursor-pointer items-center gap-2 rounded-md border p-3 text-sm transition-colors ${
                    selected ? 'border-primary bg-primary/5' : 'border-border hover:bg-muted/50'
                  }`}
                >
                  <RadioGroupItem value={opt.value} id={`add-doc-source-${opt.value}`} />
                  <span className="font-medium text-foreground">{opt.label}</span>
                </label>
              );
            })}
          </RadioGroup>

          {sourceType === 'url' ? (
            <div className="space-y-1.5">
              <Label htmlFor="add-doc-url">
                URL <span className="text-destructive">*</span>
              </Label>
              <Input
                id="add-doc-url"
                placeholder="https://example.com/help"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
              />
            </div>
          ) : (
            <div className="space-y-1.5">
              <Label>
                File <span className="text-destructive">*</span>
              </Label>
              <FileUpload
                value={file}
                onChange={handleFileChange}
                accept={KNOWLEDGE_BASE_ACCEPT}
                hint={`PDF, DOC, DOCX or TXT — up to ${MAX_FILE_LABEL}`}
                maxSize={KNOWLEDGE_BASE_MAX_FILE_SIZE_BYTES}
              />
            </div>
          )}
          {error && <p className="text-xs text-destructive">{error}</p>}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={close} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={saving}>
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            Add
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
